import { motion } from "framer-motion";
import { useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import { SectionHead, Reveal } from "@/components/Reveal";
import { ColoringGrid } from "@/components/ColoringGrid";
import { GoogleIcon, YouTubeIcon, TikTokIcon, InstagramIcon } from "@/components/icons";

export const DrawSection = () => {
  const { t } = useI18n();
  const { coloring } = useSite();
  const [theme, setTheme] = useState("");
  const list = Array.isArray(coloring) ? coloring : [];
  const themes = [...new Set(list.map((c) => c.theme).filter(Boolean))];
  const shown = theme && themes.includes(theme) ? list.filter((c) => c.theme === theme) : list;
  return (
    <section id="disegna" className="relative overflow-hidden dots-bg py-24 sm:py-32" data-testid="draw-section">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end gap-6 justify-between">
          <SectionHead eyebrow={t.draw.eyebrow} title={t.draw.title} sub={t.draw.sub} testid="draw-section-head" eyebrowClass="bg-sky !text-white" />
          <motion.img src="/brand/star.webp" alt={t.imgAlt.mascot} className="w-32 sm:w-40 self-end" animate={{ rotate: [-6, 6, -6] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
        </div>
        {themes.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="draw-theme-filters">
            {["", ...themes].map((th) => (
              <button key={th || "all"} onClick={() => setTheme(th)} className={`rounded-full border-2 border-ink px-4 py-1.5 text-sm font-extrabold transition-colors ${theme === th ? "bg-sky text-white" : "bg-white hover:bg-sky/20"}`} data-testid={`draw-theme-${th || "all"}`}>
                {th || t.games.all}
              </button>
            ))}
          </div>
        )}
        <ColoringGrid items={shown} />
      </div>
    </section>
  );
};

export const LoginButton = ({ className = "", testid = "google-login-btn" }) => {
  const { t } = useI18n();
  return (
    <Link to="/login?next=/genitori" className={`btn-pop sticker inline-flex items-center gap-3 rounded-full bg-white text-ink px-6 py-3.5 font-display font-semibold text-lg ${className}`} data-testid={testid}>
      <GoogleIcon className="w-6 h-6" /> {t.nav.login}
    </Link>
  );
};

export const ParentsTeaser = () => {
  const { t } = useI18n();
  const { user } = useSite();
  return (
    <section id="genitori" className="relative overflow-hidden bg-pink py-24 sm:py-32 grain" data-testid="parents-section">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1 relative">
          <div className="absolute inset-[8%] rounded-full bg-sun border-[3px] border-ink" />
          <motion.img
            src="/brand/heart.webp"
            alt="Ananawi"
            className="relative w-full max-w-[380px] mx-auto"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ scale: { duration: 1.2, repeat: Infinity }, default: { duration: 0.8 } }}
          />
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2 text-white">
          <SectionHead eyebrow={t.parents.eyebrow} title={t.parents.title} sub={t.parents.sub} tone="light" testid="parents-section-head" eyebrowClass="bg-white" />
          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-3">
              {t.parents.perks.map((p) => (
                <li key={p} className="flex items-center gap-3 font-bold text-base sm:text-lg">
                  <span className="grid place-items-center w-8 h-8 rounded-full bg-sun text-ink border-2 border-ink shrink-0"><Check className="w-4 h-4" strokeWidth={3} /></span>
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              {user ? (
                <Link to="/genitori" className="btn-pop sticker inline-flex items-center gap-2 rounded-full bg-sun text-ink px-6 py-3.5 font-display font-semibold text-lg" data-testid="parents-goto-btn">
                  {t.parents.goTo} <ArrowRight className="w-5 h-5" />
                </Link>
              ) : (
                <LoginButton testid="parents-login-btn" />
              )}
              <span className="text-sm font-bold text-white/90">{t.parents.adults}</span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const SOCIALS = [
  { key: "youtube", name: "YouTube", handle: "@ananawi", Icon: YouTubeIcon, bg: "#ff0000", pose: "/brand/winkwave.webp" },
  { key: "tiktok", name: "TikTok", handle: "@ananawi_it", Icon: TikTokIcon, bg: "#1f1a3d", pose: "/brand/jump.webp" },
  { key: "instagram", name: "Instagram", handle: "@ananawi_it", Icon: InstagramIcon, bg: "#d62976", pose: "/brand/wave3q.webp" },
];

export const SocialSection = () => {
  const { t } = useI18n();
  const { content } = useSite();
  return (
    <section id="social" className="relative overflow-hidden bg-sky py-24 sm:py-32" data-testid="social-section">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow={t.social.eyebrow} title={t.social.title} sub={t.social.sub} testid="social-section-head" eyebrowClass="bg-sun" />
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8">
          {SOCIALS.map(({ key, name, handle, Icon, bg, pose }, i) => (
            <motion.a
              key={key}
              href={content?.socials?.[key]}
              target="_blank"
              rel="noopener noreferrer"
              data-social-platform={key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              whileHover={{ y: -10, rotate: i === 1 ? 0 : i ? 2 : -2 }}
              className="group relative rounded-[30px] sticker p-7 pt-24 text-white"
              style={{ background: bg }}
              data-testid={`social-link-${key}`}
            >
              <img src={pose} alt={t.imgAlt.mascot} className="absolute -top-14 right-6 w-28 transition-transform duration-500 group-hover:-translate-y-3 group-hover:rotate-6" />
              <Icon className="w-12 h-12" />
              <p className="mt-5 font-display font-bold text-3xl">{name}</p>
              <p className="font-bold text-white/90">{handle}</p>
              <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-ink px-4 py-2 font-extrabold text-sm border-2 border-ink">
                {t.social.follow} <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
